import { Notification, NotificationImportance, NotificationStatus } from 'src/graphql/generated/schema';

/**
 * How many unread cards the expanded home panel shows. The header count and the
 * archive button carry the rest.
 */
export const HOME_UNREAD_CARD_LIMIT = 2;

/** Lower rank sorts first. */
export const SEVERITY_RANK: Record<NotificationImportance, number> = {
  [NotificationImportance.Critical]: 0,
  [NotificationImportance.Medium]: 1,
  [NotificationImportance.Low]: 2,
};

/** Anything unrecognised sorts after every known severity rather than yielding NaN. */
const UNKNOWN_RANK = Number.MAX_SAFE_INTEGER;

function rankOf(notification: Notification): number {
  return SEVERITY_RANK[notification.importance] ?? UNKNOWN_RANK;
}

function time(notification: Notification): number {
  // createdAt is `any` in the generated types and the list is cast straight from
  // an unvalidated response, so a null or malformed value is possible. NaN would
  // make every comparison involving that row 0, silently disabling the date
  // tiebreak for its whole severity group - same reasoning as UNKNOWN_RANK.
  const parsed = new Date(notification.createdAt).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

function isUnread(notification: Notification): boolean {
  return notification.status === NotificationStatus.NotRead;
}

/**
 * Unread-list order: Critical -> Medium -> Low, newest first within a severity.
 *
 * Callers pass unread-only lists (the widget and the archive's Unread section),
 * but the read/unread check is kept as a precondition guard so a mixed list can
 * never interleave read rows among unread ones.
 */
export function sortByUnreadPriority(list: Notification[]): Notification[] {
  return [...list].sort((a, b) => {
    if (isUnread(a) !== isUnread(b)) return isUnread(a) ? -1 : 1;
    const rank = rankOf(a) - rankOf(b);
    if (rank !== 0) return rank;
    return time(b) - time(a);
  });
}

/**
 * Read-list order: date received, newest first, and nothing else. Severity is
 * shown on the card but never moves a row.
 */
export function sortByDateDesc(list: Notification[]): Notification[] {
  return [...list].sort((a, b) => time(b) - time(a));
}
